import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  Container, Box, Typography, Button, CircularProgress, IconButton, Tooltip, 
  List, ListItem, ListItemText, Paper, ListItemIcon, Snackbar
} from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON } from 'react-leaflet';
import api from './api';

// --- IMPORTS FOR ROUTING & UI ---
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import MapEvents from './MapEvents'; 
import FullscreenButton from './FullscreenButton';
import MapLegend from './MapLegend';
import PhotoGallery from './PhotoGallery'; 

// Icons
import { redIcon, blueIcon, greenIcon, orangeIcon, yellowIcon, fireworksIcon } from './icons'; 
import DeleteIcon from '@mui/icons-material/Delete'; 
import MyLocationIcon from '@mui/icons-material/MyLocation';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import UndoIcon from '@mui/icons-material/Undo';
import NavigationIcon from '@mui/icons-material/Navigation';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary'; 
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; 
import AddHomeIcon from '@mui/icons-material/AddHome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Helper to capitalize words
const toTitleCase = (str) => {
  return str.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

const fireworksLocations = [
  { id: 'fw1', name: 'Sydney Harbour', lat: -33.8568, lng: 151.2153, time: '9pm & Midnight' },
  { id: 'fw2', name: 'Melbourne CBD (Docklands)', lat: -37.8150, lng: 144.9460, time: '9:30pm & Midnight' },
  { id: 'fw3', name: 'Brisbane (South Bank)', lat: -27.4772, lng: 153.0233, time: '8pm & Midnight' },
  { id: 'fw4', name: 'Perth (Elizabeth Quay)', lat: -31.9566, lng: 115.8575, time: '8pm & Midnight' },
  { id: 'fw5', name: 'Adelaide (Elder Park)', lat: -34.9196, lng: 138.5997, time: '9pm & Midnight' },
  { id: 'fw6', name: 'Geelong Waterfront', lat: -38.1439, lng: 144.3644, time: '9:30pm & Midnight' },
  { id: 'fw7', name: 'Ballarat (Lake Wendouree)', lat: -37.5501, lng: 143.8340, time: '9:30pm' },
];

function SuburbPage() {
  // --- AUTOMATIC YEAR VARIABLE ---
  const currentYear = new Date().getFullYear();
  // -------------------------------

  const { state, suburb, postcode } = useParams();
  const navigate = useNavigate();
  
  const [locationData, setLocationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [displays, setDisplays] = useState([]);
  const [mySubmissions, setMySubmissions] = useState([]);
  
  const [map, setMap] = useState(null);
  const [myPosition, setMyPosition] = useState(null); 
  const [waypoints, setWaypoints] = useState([]);
  const [postcodeData, setPostcodeData] = useState(null); 
  
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedDisplay, setSelectedDisplay] = useState(null);

  const [visibility, setVisibility] = useState({
    public: true,
    myCompleted: true,
    myPending: true,
    route: true,
    myLocation: true,
    postcodes: false,
    fireworks: true,
  });

  const [visitedIds, setVisitedIds] = useState([]);
  // eslint warning fix: keeping setShowConfetti active but silencing the unused variable
  const [, setShowConfetti] = useState(false);

  const handleMarkVisited = (id) => {
    if (!visitedIds.includes(id)) {
      setVisitedIds([...visitedIds, id]);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000);
    }
  };

  useEffect(() => {
    fetch('/Suburbs.json') 
      .then(response => response.text()) 
      .then(text => {
        const lines = text.split('\n');
        let found = null;
        let backupFound = null;

        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;
          const parts = line.split(',');
          if (parts.length < 5) continue;

          const rowSuburb = parts[0].trim();
          const rowState = parts[1].trim();
          const rowPostcode = parts[2].trim();
          const rowLat = parseFloat(parts[3]);
          const rowLng = parseFloat(parts[4]);

          if (rowSuburb.toLowerCase() === suburb.toLowerCase() &&
              rowState.toLowerCase() === state.toLowerCase() &&
              rowPostcode === postcode) {
            found = { suburb: rowSuburb, state: rowState, postcode: rowPostcode, lat: rowLat, lng: rowLng };
            break; 
          }
          if (rowState.toLowerCase() === state.toLowerCase() && rowPostcode === postcode && !backupFound) {
            backupFound = { suburb: rowSuburb, state: rowState, postcode: rowPostcode, lat: rowLat, lng: rowLng };
          }
        }

        if (found) {
          setLocationData(found);
          updateMeta(found.suburb, found.state, found.postcode);
        } else if (backupFound) {
          const formattedSuburb = toTitleCase(suburb);
          setLocationData({ ...backupFound, suburb: formattedSuburb });
          updateMeta(formattedSuburb, backupFound.state, backupFound.postcode);
        } else {
          setError('Location not found.');
        }
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError('Error loading location data.');
        setLoading(false);
      });
      
      fetch('/postcodes.geojson') 
        .then(response => response.json())
        .then(data => setPostcodeData(data))
        .catch(err => console.error("Error fetching postcode geojson:", err));

      const token = localStorage.getItem('token');
      if (token) {
        api.get('/displays/me')
           .then(res => setMySubmissions(res.data))
           .catch(err => console.error(err));
           
        api.get('/route')
           .then(res => {
             if (res.data && res.data.length > 0) {
               setWaypoints(res.data);
               setToastMessage('Route loaded from cloud!');
               setToastOpen(true);
             }
           })
           .catch(err => console.error("Error fetching route", err));
      }
  // eslint warning fix: ignoring the missing updateMeta dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suburb, state, postcode, currentYear]);

  useEffect(() => {
    const fetchDisplays = async () => {
      try {
        const response = await api.get('/displays/verified');
        setDisplays(response.data);
      } catch (err) {
        console.error("Error fetching displays", err);
      }
    };
    fetchDisplays();
  }, []);

  const updateMeta = (subName, subState, subPost) => {
    document.title = `Christmas Lights in ${subName}, ${subState} ${subPost} | ${currentYear} Map`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', `Find the best ${currentYear} Christmas light displays in ${subName}, ${subState} ${subPost}. View photos, get directions, and plan your route.`);
    }
  }
  
  const handleVisibilityToggle = (type) => {
    setVisibility(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const findMyLocal = () => { if (map) map.locate(); };

  const handleSaveRoute = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/add');
      return;
    }
    try {
      await api.put('/route', { waypoints });
      setToastMessage('Route saved successfully!');
      setToastOpen(true);
    } catch (err) {
      console.error(err);
      alert("Failed to save route.");
    }
  };

  const clearWaypoints = () => {
    if(window.confirm("Are you sure you want to clear your route?")) {
      setWaypoints([]);
    }
  };

  const removeLastWaypoint = () => { setWaypoints(waypoints.slice(0, -1)); };

  const removeWaypointAtIndex = (indexToRemove) => {
    setWaypoints(waypoints.filter((_, index) => index !== indexToRemove));
  };

  const addWaypoint = (lat, lng, name, photos = [], id) => {
    const newWaypoint = {
      lat, lng, name, photos, id,
      uuid: `${id}-${Date.now()}-${Math.random()}`
    };
    setWaypoints(prev => [...prev, newWaypoint]);
    setToastMessage("Added to Route!");
    setToastOpen(true);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(waypoints);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setWaypoints(items);
  };

  const handleStartDrive = () => {
    let routePoints = [];
    if (myPosition) {
      routePoints.push(`${myPosition.lat},${myPosition.lng}`);
    }
    waypoints.forEach(pos => {
      routePoints.push(`${pos.lat},${pos.lng}`);
    });

    if (routePoints.length < 1 && locationData) {
        window.open(`https://www.google.com/maps/search/?api=1&query=${locationData.lat},${locationData.lng}`, '_blank');
        return;
    }

    const baseUrl = 'https://www.google.com/maps/dir/';
    
    if (routePoints.length === 1) {
       window.open(`${baseUrl}?api=1&destination=${routePoints[0]}`, '_blank');
       return;
    }
    
    const origin = routePoints.shift(); 
    const destination = routePoints.pop(); 
    let url = `${baseUrl}?api=1&origin=${origin}&destination=${destination}`;
    if (routePoints.length > 0) {
      const intermediateWaypoints = routePoints.join('|');
      url += `&waypoints=${intermediateWaypoints}`;
    }
    window.open(url, '_blank');
  };

  const suburbBoundary = useMemo(() => {
    if (!postcodeData || !locationData || !locationData.suburb) return null;

    const matchingFeature = postcodeData.features.find(f => {
      const p = f.properties;
      const suburbName = 
        p.act_loca_2 || p.wa_local_2 || p.vic_loca_2 || 
        p.nsw_loca_2 || p.qld_loca_2 || p.sa_loca_2 || 
        p.nt_loca_2 || p.tas_loca_2;
      
      return suburbName && suburbName.toLowerCase() === locationData.suburb.toLowerCase();
    });

    return matchingFeature ? { type: "FeatureCollection", features: [matchingFeature] } : null;
  }, [postcodeData, locationData]);

  const onEachFeature = (feature, layer) => {
      const properName = locationData.suburb.toLowerCase().replace(/\b\w/g, s => s.toUpperCase());
      layer.bindTooltip(`${properName} Boundary`);
  };
  
  const handleOpenGallery = (display) => {
    setSelectedDisplay(display);
    setGalleryOpen(true);
  };
  const handleCloseGallery = () => {
    setGalleryOpen(false);
    setSelectedDisplay(null);
  };
  const handleViewPhotosFromRoute = (waypoint) => {
    const displayData = {
      address: waypoint.name,
      photos: waypoint.photos || []
    };
    handleOpenGallery(displayData);
  };

  if (loading) return <Container sx={{ mt: 10, textAlign: 'center' }}><CircularProgress /></Container>;
  if (error || !locationData) return <Container sx={{ mt: 10, textAlign: 'center' }}><Typography variant="h5" color="error">{error || 'Suburb not found.'}</Typography><Button component={RouterLink} to="/" variant="contained" sx={{ mt: 2 }}>Return to Map</Button></Container>;

  const suburbSchema = {
    "@context": "https://schema.org",
    "@type": "TouristDestination",
    "name": `Christmas Lights in ${locationData.suburb}`,
    "description": `Best Christmas light displays and festive houses in ${locationData.suburb}, ${locationData.state} in ${currentYear}.`,
    "image": [
      "https://christmas.termplanner.com.au/logo512.png",
      "https://christmas.termplanner.com.au/share-image.png"
    ],
    "address": {
      "@type": "PostalAddress",
      "addressLocality": locationData.suburb,
      "addressRegion": locationData.state,
      "postalCode": locationData.postcode,
      "addressCountry": "AU"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": locationData.lat,
      "longitude": locationData.lng
    }
  };

  return (
    <Container maxWidth="lg">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(suburbSchema) }} />

      <Box sx={{ my: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Christmas Lights in {locationData.suburb}
        </Typography>
        
        <Typography variant="h5" color="textSecondary" gutterBottom>
          {locationData.state} {locationData.postcode}
        </Typography>

        <Box 
          component="img"
          src="/share-image.png" 
          alt={`Christmas lights map showing displays in ${locationData.suburb} ${locationData.state}`}
          sx={{
            width: '150px', height: 'auto',
            borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', mb: 4, mt: 2
          }}
        />

        <Typography variant="body1" paragraph sx={{ maxWidth: '800px', mx: 'auto', mb: 4 }}>
          Looking for the best Christmas light displays in <strong>{locationData.suburb}</strong>? 
          Our community-driven map helps you find festive houses near you in {locationData.suburb}, {locationData.state}.
          Use the map below to plan your route!
        </Typography>

        {/* --- LIVE FUEL PRICE INTEGRATION BANNER --- */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 3, 
            mb: 4, 
            maxWidth: '900px',
            mx: 'auto',
            borderRadius: 3, 
            background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
            border: '1px solid #ffb74d',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            textAlign: { xs: 'center', sm: 'left' },
            gap: 2.5
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontSize: '38px', lineHeight: 1 }}>⛽</Typography>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#e65100', mb: 0.5 }}>
                Planning your {locationData.suburb} lights tour?
              </Typography>
              <Typography variant="body2" sx={{ color: '#4a4a4a' }}>
                Don't waste money idling in traffic. Compare live petrol and diesel prices in {locationData.suburb} ({locationData.postcode}) before heading out.
              </Typography>
            </Box>
          </Box>
          
          <Button 
            variant="contained" 
            size="large"
            href={`https://termplanner.com.au/petrol-prices/${locationData.state.toLowerCase()}/${locationData.suburb.toLowerCase().replace(/\s+/g, '-')}/${locationData.postcode}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ 
              whiteSpace: 'nowrap', 
              fontWeight: 'bold',
              backgroundColor: '#e65100',
              flexShrink: 0,
              boxShadow: '0 4px 14px rgba(230, 81, 0, 0.35)',
              '&:hover': { backgroundColor: '#d84315' }
            }}
          >
            View {locationData.suburb} Fuel Prices
          </Button>
        </Paper>
        {/* ------------------------------------------- */}

        <Box sx={{ width: '100%', mb: 2, display: { xs: 'none', md: 'flex' }, flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            <Button variant="contained" onClick={findMyLocal}>Find My Location</Button>
            <Button variant="outlined" color="secondary" onClick={clearWaypoints} disabled={waypoints.length === 0 && !myPosition}>Clear Route</Button>
            <Button variant="outlined" color="success" onClick={handleSaveRoute} startIcon={<CloudUploadIcon />}>Save Route</Button>
            <Button variant="outlined" color="warning" onClick={removeLastWaypoint} disabled={waypoints.length === 0}>Remove Last Stop</Button>
            <Button 
              variant="contained" 
              color="error" 
              component={RouterLink} 
              to="/add" 
              startIcon={<AddHomeIcon />}
            >
              Add My Display
            </Button>
            <Button variant="contained" color="primary" onClick={handleStartDrive} sx={{ ml: 'auto' }}>Start Navigation</Button>
        </Box>

        <Typography variant="caption" sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 1, fontStyle: 'italic' }}>Hold finger on icon to display function</Typography>
        <Box sx={{ width: '100%', mb: 2, display: { xs: 'flex', md: 'none' }, justifyContent: 'space-between', alignItems: 'center' }}>
            <Tooltip title="Find My Location" placement="top"><IconButton color="primary" variant="contained" onClick={findMyLocal}><MyLocationIcon /></IconButton></Tooltip>
            <Tooltip title="Clear Route" placement="top"><span><IconButton color="secondary" onClick={clearWaypoints} disabled={waypoints.length === 0 && !myPosition}><DeleteForeverIcon /></IconButton></span></Tooltip>
            <Tooltip title="Save Route" placement="top"><span><IconButton color="success" onClick={handleSaveRoute}><CloudUploadIcon /></IconButton></span></Tooltip>
            <Tooltip title="Remove Last Stop" placement="top"><span><IconButton color="warning" onClick={removeLastWaypoint} disabled={waypoints.length === 0}><UndoIcon /></IconButton></span></Tooltip>
            <Tooltip title="Add My Display" placement="top">
              <IconButton color="error" component={RouterLink} to="/add">
                <AddHomeIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Start Navigation" placement="top"><span><IconButton color="primary" variant="contained" onClick={handleStartDrive}><NavigationIcon /></IconButton></span></Tooltip>
        </Box>

        <Box 
          sx={{ 
            height: '500px', 
            width: '100%', 
            borderRadius: '8px', 
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            mb: 4,
            position: 'relative',
            '& .leaflet-top.leaflet-left': {
              top: '50%', 
              transform: 'translateY(-50%)' 
            }
          }}
        >
          <MapContainer 
            center={[locationData.lat, locationData.lng]} 
            zoom={13} 
            style={{ height: '100%', width: '100%' }}
            key={`${locationData.lat}-${locationData.lng}`} 
            ref={setMap} 
          >
            <MapEvents setWaypoints={setWaypoints} setMyPosition={setMyPosition} />
            
            <TileLayer
              attribution='© OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <FullscreenButton />
            <MapLegend visibility={visibility} onToggle={handleVisibilityToggle} />

            {visibility.postcodes && suburbBoundary && (
                <GeoJSON 
                  key={locationData.suburb}
                  data={suburbBoundary} 
                  style={{ color: 'red', weight: 2, fillOpacity: 0.1 }}
                  onEachFeature={onEachFeature}
                />
            )}

            <Marker position={[locationData.lat, locationData.lng]} icon={blueIcon}>
              <Popup>
                <strong>{locationData.suburb} Center</strong><br />
                {locationData.postcode}
              </Popup>
            </Marker>

            {visibility.public && displays.map(display => (
              <Marker 
                key={display.id} 
                position={[display.latitude, display.longitude]} 
                icon={redIcon}
                eventHandlers={{
                  click: () => addWaypoint(parseFloat(display.latitude), parseFloat(display.longitude), `${display.first_name}'s Display`, display.photos, display.id),
                }}
              >
                <Popup>
                  <strong>{display.first_name || 'Display'}</strong><br />
                  {display.address}<br/>
                  {display.photos && display.photos.length > 0 && (
                    <Button size="small" startIcon={<PhotoLibraryIcon />} onClick={() => handleOpenGallery(display)} sx={{ p: 0, mt: 1, textTransform: 'none', width: '100%' }}>
                      View Photos ({display.photos.length})
                    </Button>
                  )}
                  <Button variant={visitedIds.includes(display.id) ? "outlined" : "contained"} color={visitedIds.includes(display.id) ? "success" : "primary"} size="small" startIcon={visitedIds.includes(display.id) ? <CheckCircleIcon /> : null} sx={{ mt: 1, width: '100%' }} onClick={() => handleMarkVisited(display.id)}>{visitedIds.includes(display.id) ? "Visited!" : "Mark as Visited"}</Button>
                </Popup>
              </Marker>
            ))}

            {mySubmissions.map(display => {
                const isCompleted = display.payment_status === 'completed';
                if ((isCompleted && !visibility.myCompleted) || (!isCompleted && !visibility.myPending)) return null;
                const icon = isCompleted ? yellowIcon : orangeIcon;
                return (
                  <Marker 
                    key={display.id} 
                    position={[parseFloat(display.latitude), parseFloat(display.longitude)]} 
                    icon={icon} 
                    eventHandlers={{ click: () => addWaypoint(parseFloat(display.latitude), parseFloat(display.longitude), 'My Submission', display.photos || [], display.id) }}
                  >
                    <Popup>
                        <strong>My Submission</strong><br />{display.address}<br /><em>Status: {display.payment_status}</em><br />
                        {display.photos && display.photos.length > 0 && (
                          <Button size="small" startIcon={<PhotoLibraryIcon />} onClick={() => handleOpenGallery(display)} sx={{ p: 0, mt: 1, textTransform: 'none', width: '100%' }}>
                            View Photos ({display.photos.length})
                          </Button>
                        )}
                        <Button variant={visitedIds.includes(display.id) ? "outlined" : "contained"} color={visitedIds.includes(display.id) ? "success" : "primary"} size="small" startIcon={visitedIds.includes(display.id) ? <CheckCircleIcon /> : null} sx={{ mt: 1, width: '100%' }} onClick={() => handleMarkVisited(display.id)}>{visitedIds.includes(display.id) ? "Visited!" : "Mark as Visited"}</Button>
                    </Popup>
                  </Marker>
                );
            })}

            {visibility.myLocation && myPosition && (
              <Marker position={myPosition} icon={blueIcon}> 
                <Popup>You are here!</Popup>
              </Marker>
            )}
            
            {visibility.route && waypoints.map((pos, idx) => (
                <Marker key={idx} position={[pos.lat, pos.lng]} icon={greenIcon}> 
                  <Popup>{pos.name ? pos.name : `Stop ${idx + 1}`}</Popup>
                </Marker>
            ))}
            
            {visibility.fireworks && fireworksLocations.map(fw => (
              <Marker key={fw.id} position={[fw.lat, fw.lng]} icon={fireworksIcon}>
                <Popup>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>🎆 {fw.name} NYE Fireworks</Typography>
                  <Typography variant="body2">Times: {fw.time}</Typography>
                  <Button size="small" component={RouterLink} to={`/blog/new-years-eve-fireworks-${currentYear}`} sx={{ mt: 1 }}>
                    View Event Guide
                  </Button>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </Box>

        {waypoints.length > 0 && (
            <Box sx={{ width: '100%', mt: 3, textAlign: 'left' }}> 
              <Typography variant="h6">My Route ({waypoints.length} stops):</Typography>
              
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="route-list">
                  {(provided) => (
                    <List dense {...provided.droppableProps} ref={provided.innerRef}>
                      {waypoints.map((pos, idx) => {
                        const uniqueKey = pos.uuid || `item-${idx}`;
                        return (
                          <Draggable key={uniqueKey} draggableId={uniqueKey} index={idx}>
                            {(provided) => (
                              <Paper ref={provided.innerRef} {...provided.draggableProps} elevation={1} sx={{ mb: 1, backgroundColor: '#fff' }}>
                                <ListItem secondaryAction={
                                    <Box>
                                      {pos.photos && (
                                        <IconButton edge="end" onClick={() => handleViewPhotosFromRoute(pos)}>
                                          <PhotoLibraryIcon />
                                        </IconButton>
                                      )}
                                      <IconButton edge="end" aria-label="delete" onClick={() => removeWaypointAtIndex(idx)}>
                                          <DeleteIcon />
                                      </IconButton>
                                    </Box>
                                  }>
                                  <ListItemIcon {...provided.dragHandleProps} sx={{ minWidth: '30px', cursor: 'grab' }}><DragHandleIcon /></ListItemIcon>
                                  <ListItemText 
                                    primary={pos.name && pos.name !== 'Custom Waypoint' ? `Stop ${idx + 1}: ${pos.name}` : `Stop ${idx + 1}`} 
                                    secondary={`(${pos.lat.toFixed(4)}, ${pos.lng.toFixed(4)})`} 
                                  />
                                </ListItem>
                              </Paper>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </DragDropContext>
            </Box>
        )}

        <Button variant="contained" size="large" component={RouterLink} to="/">
          View Full Australia Map
        </Button>

        {selectedDisplay && (
          <PhotoGallery 
            open={galleryOpen} 
            onClose={handleCloseGallery} 
            photos={selectedDisplay.photos || []} 
            displayName={selectedDisplay.address} 
          />
        )}

        <Snackbar
          open={toastOpen}
          autoHideDuration={3000}
          onClose={() => setToastOpen(false)}
          message={toastMessage}
        />

      </Box>
    </Container>
  );
}

export default SuburbPage;